import { useEffect, useState } from "react";
import { Check, RefreshCw, Wifi, WifiOff, Clock, Database } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { subscribe, markSyncing } from "@/lib/syncStatus";

function formatAgo(ts) {
  if (!ts) return null;
  const diffSec = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (diffSec < 5) return "ahora";
  if (diffSec < 60) return `hace ${diffSec}s`;
  const mins = Math.round(diffSec / 60);
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `hace ${hrs} h`;
  const days = Math.round(hrs / 24);
  return `hace ${days} d`;
}

function formatExact(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("es-MX", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return new Date(ts).toString();
  }
}

export function SyncIndicator() {
  const [s, setS] = useState({ lastSyncAt: null, syncing: 0 });
  const [online, setOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  const [, force] = useState(0);

  useEffect(() => subscribe(setS), []);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Re-render every 30s to update "hace X min"
  useEffect(() => {
    const id = setInterval(() => force((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  if (!online) return null;
  if (!s.lastSyncAt && s.syncing === 0) return null;

  const syncing = s.syncing > 0;
  const label = syncing ? "Sincronizando…" : `Sincronizado ${formatAgo(s.lastSyncAt)}`;
  // TODO: when offline order queue is implemented, expose its count via syncStatus.
  const pendingCount = 0;

  return (
    <div
      data-testid="sync-indicator"
      className="fixed z-40 top-3 left-1/2 -translate-x-1/2 select-none"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
      }}
    >
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid="sync-indicator-trigger"
            className={`inline-flex items-center gap-2 px-3 h-8 rounded-full bg-white/95 backdrop-blur border border-[#b182ec]/30 shadow-sm text-[11px] font-medium text-[#3e166c] transition-all hover:shadow-md hover:bg-white ${syncing ? "opacity-100" : "opacity-80 hover:opacity-100"}`}
            aria-label="Estado de sincronización"
          >
            {syncing ? (
              <RefreshCw className="h-3 w-3 text-[#7248ae] animate-spin" />
            ) : (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
            )}
            <span>{label}</span>
          </button>
        </PopoverTrigger>

        <PopoverContent
          side="bottom"
          align="center"
          className="w-72 p-0 border-[#b182ec]/40 shadow-xl"
          data-testid="sync-indicator-panel"
        >
          <div className="bg-[#1a0f2e] text-white px-4 py-3 rounded-t-md">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#b182ec]">Conexión</p>
            <p className="font-display text-lg font-bold leading-tight mt-1">Estado en vivo</p>
          </div>
          <div className="p-4 space-y-3 text-sm text-[#1a0f2e]">
            <div className="flex items-start gap-3">
              <span className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full ${online ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                {online ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
              </span>
              <div>
                <p className="font-semibold">{online ? "En línea" : "Sin conexión"}</p>
                <p className="text-xs text-[#7248ae]">
                  {online ? "Todos los cambios se guardan al instante." : "Modo solo lectura activo."}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#3e166c]/10 text-[#3e166c]">
                <Clock className="h-4 w-4" />
              </span>
              <div>
                <p className="font-semibold">Última sincronización</p>
                <p className="text-xs text-[#7248ae]">{formatExact(s.lastSyncAt)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className={`mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full ${pendingCount > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                {pendingCount > 0 ? <Database className="h-4 w-4" /> : <Check className="h-4 w-4" />}
              </span>
              <div>
                <p className="font-semibold">
                  {pendingCount > 0
                    ? `${pendingCount} ${pendingCount === 1 ? "ítem" : "ítems"} en cola`
                    : "Todo guardado en la nube"}
                </p>
                <p className="text-xs text-[#7248ae]">
                  {pendingCount > 0
                    ? "Se enviarán al volver el WiFi."
                    : "Sin pedidos pendientes de subir."}
                </p>
              </div>
            </div>
          </div>
          <div className="border-t border-[#b182ec]/20 p-3 bg-[#fcfbfd] rounded-b-md">
            <button
              type="button"
              data-testid="sync-force-button"
              disabled={!online || syncing}
              onClick={() => {
                if (!online || syncing) return;
                markSyncing();
                // Soft hint for any page listening for a manual resync trigger.
                try { window.dispatchEvent(new CustomEvent("orden:resync")); } catch (_) { /* ignore */ }
                // Hard guarantee: reload current route to refetch all data.
                setTimeout(() => window.location.reload(), 120);
              }}
              className="w-full inline-flex items-center justify-center gap-2 h-9 rounded-lg bg-[#3e166c] text-white text-xs font-semibold hover:bg-[#2a0e4a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Sincronizando…" : (online ? "Forzar sincronización" : "Sin conexión")}
            </button>
            <p className="mt-2 text-center text-[9px] uppercase tracking-[0.2em] text-[#7248ae]">
              oRdeN sync
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
